import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { ShieldCheck, Lock } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import api from '@/api';

// One auto-SSO attempt per page/tab load (module state resets on full reload).
let autoSsoTriggered = false;

function formatError(detail) {
  if (!detail) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}

export default function LoginPage() {
  const { user, login, ssoChecking, loading: authLoading, masterSessionAvailable } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [autoSso, setAutoSso] = useState(false);

  useEffect(() => { document.title = 'Radar — Login'; }, []);

  // Auto-trigger the SAME flow as the "Login with SSO" button, once per tab
  // load, only when the silent check found a valid Master session. Any
  // failure → silently stay on this page (no error banner).
  useEffect(() => {
    if (autoSsoTriggered) return;
    if (authLoading || ssoChecking) return;   // wait for boot + silent check
    if (user) return;                          // already authenticated
    if (!masterSessionAvailable) return;       // no Master session → stay
    autoSsoTriggered = true;
    setAutoSso(true);
    (async () => {
      try {
        const r = await api.get('/auth/login-url');
        const url = r?.data?.url;
        if (!url) throw new Error('No SSO URL returned');
        window.location.href = url;
      } catch {
        setAutoSso(false); // silent — normal login page remains
      }
    })();
  }, [user, authLoading, ssoChecking, masterSessionAvailable]);

  if (user) return <Navigate to="/" />;

  // One silent Master SSO check per tab — while it runs, hide the form.
  // On failure we fall through to the normal login page below, unchanged.
  if (ssoChecking || autoSso) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" data-testid="sso-checking">
        <div className="text-center">
          <ShieldCheck size={32} weight="duotone" className="text-[#0D7377] mx-auto mb-4" />
          <div className="w-8 h-8 border-2 border-[#1B2A4A] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-[#52525B]">Checking Master SSO session…</p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSsoClick = async () => {
    setError('');
    setSsoLoading(true);
    try {
      const r = await api.get('/auth/login-url');
      const url = r?.data?.url;
      if (!url) throw new Error('No SSO URL returned');
      window.location.href = url;
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || 'SSO is not available right now.');
      setSsoLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img src="https://images.pexels.com/photos/5058200/pexels-photo-5058200.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Corporate skyline" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1B2A4A]/90 to-[#1B2A4A]/60" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <ShieldCheck size={48} weight="duotone" className="mb-4 text-[#0D7377]" />
          <h1 className="text-4xl font-bold tracking-[4px] mb-1" style={{color: '#fff'}}>RADAR</h1>
          <p className="text-sm text-[#0D7377] mb-4">Risk Assessment, Detection & Automated Review</p>
          <p className="text-lg text-white/80 max-w-md leading-relaxed">
            AML Screening, PEP Intelligence & Media Monitoring Platform
          </p>
          <div className="flex gap-6 mt-6 text-sm text-white/60">
            <span>Sanctions Screening</span>
            <span>PEP Identification</span>
            <span>Ongoing Monitoring</span>
          </div>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex items-center justify-center bg-white p-8">
        <div className="w-full max-w-[380px]">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-2">
              <ShieldCheck size={32} weight="duotone" className="text-[#0D7377]" />
              <span className="text-3xl font-bold tracking-[4px]" style={{color: '#1B2A4A'}}>RADAR</span>
            </div>
            <p className="text-sm" style={{color: '#0D7377'}}>Risk Assessment, Detection & Automated Review</p>
            <p className="text-xs text-gray-500 italic mt-1">Part of the Tychi Ecosystem</p>
            <p className="text-xs text-gray-500">by Fundtec Services LLP</p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600" data-testid="login-error">{error}</div>
          )}

          <Button
            type="button"
            onClick={handleSsoClick}
            disabled={ssoLoading || loading}
            className="w-full h-11 flex items-center justify-center gap-2 bg-white text-[#1B2A4A] border border-[#1B2A4A] hover:bg-[#F7F7F8]"
            data-testid="login-sso-btn"
          >
            <Lock size={16} weight="duotone" />
            {ssoLoading ? 'Redirecting…' : 'Login with SSO'}
          </Button>

          <div className="flex items-center gap-3 my-5" data-testid="login-divider">
            <div className="h-px flex-1 bg-gray-200" />
            <span className="text-[10px] uppercase tracking-[0.18em] text-gray-400">or sign in with email</span>
            <div className="h-px flex-1 bg-gray-200" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#52525B] mb-1.5 block">Email</label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" required data-testid="login-email-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#52525B] mb-1.5 block">Password</label>
              <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter password" required data-testid="login-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 text-white" style={{backgroundColor: '#1B2A4A'}} data-testid="login-submit-btn">
              {loading ? 'Please wait...' : 'Login'}
            </Button>
          </form>

          <div className="mt-8 pt-4 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-400">© 2026 Fundtec Services LLP</p>
            <p className="text-[10px] text-gray-400 mt-0.5">Radar and Tychi are products of Fundtec Services LLP</p>
          </div>
        </div>
      </div>
    </div>
  );
}
